import { Root, Thumb, Track } from '@radix-ui/react-slider'
import { useState } from 'react'
import { type ValueHistogram, zoneIndexOf } from 'utils'

const HISTOGRAM_BARS = 80
const FALLBACK_COLOR = '#d1d5db'

const toBars = (histogram: ValueHistogram) => {
  const perBar = Math.ceil(histogram.counts.length / HISTOGRAM_BARS)
  const binWidth = (histogram.maximum - histogram.minimum) / histogram.counts.length

  const bars = Array.from({ length: Math.ceil(histogram.counts.length / perBar) }, (_, bar) => {
    const counts = histogram.counts.slice(bar * perBar, (bar + 1) * perBar)
    return {
      center: histogram.minimum + (bar + 0.5) * perBar * binWidth,
      count: counts.reduce((total, count) => total + count, 0)
    }
  })

  const tallest = Math.max(1, ...bars.map(({ count }) => count))
  return bars.map((bar) => ({ ...bar, height: Math.sqrt(bar.count / tallest) }))
}

export const ZoneBreakSlider = ({
  histogram,
  breaks,
  colors,
  formatValue,
  onCommit
}: {
  histogram: ValueHistogram
  breaks: number[]
  colors: string[]
  formatValue: (value: number) => string
  onCommit: (breaks: number[]) => void
}) => {
  const [dragging, setDragging] = useState<number[]>()
  const values = dragging ?? breaks

  const { minimum, maximum } = histogram
  const span = maximum - minimum
  const percentOf = (value: number) => ((value - minimum) / span) * 100
  const binWidth = span / histogram.counts.length
  const toBin = (value: number) => Math.round((value - minimum) / binWidth)
  const toValue = (bin: number) => minimum + bin * binWidth
  const colorOf = (zone: number) => colors[zone] ?? FALLBACK_COLOR

  const bars = toBars(histogram)
  const barWidth = 100 / bars.length
  const edges = [minimum, ...values, maximum]
  const segments = edges.slice(1).map((edge, zone) => ({
    zone,
    left: percentOf(edges[zone]),
    width: percentOf(edge) - percentOf(edges[zone]),
    color: colorOf(zone)
  }))

  return (
    <div className="flex flex-col gap-1 px-3">
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-16 w-full">
        {bars.map((bar, index) => (
          <rect
            key={bar.center}
            x={index * barWidth}
            y={40 - bar.height * 40}
            width={barWidth * 0.9}
            height={bar.height * 40}
            fill={colorOf(zoneIndexOf({ value: bar.center, breaks: values }))}
          />
        ))}
      </svg>

      <Root
        className="relative flex h-7 w-full touch-none select-none items-center"
        value={values.map(toBin)}
        onValueChange={(bins) => setDragging(bins.map(toValue))}
        onValueCommit={(bins) => {
          setDragging(undefined)
          const moved = bins.some((bin, index) => bin !== toBin(breaks[index]))
          if (moved) onCommit(bins.map(toValue))
        }}
        min={0}
        max={histogram.counts.length}
        step={1}
        minStepsBetweenThumbs={1}
      >
        <Track className="relative h-2 grow overflow-hidden rounded-full bg-gray-200">
          {segments.map(({ zone, left, width, color }) => (
            <span
              key={zone}
              className="absolute h-full"
              style={{ left: `${left}%`, width: `${width}%`, background: color }}
            />
          ))}
        </Track>
        {values.map((value, index) => (
          <Thumb
            key={index}
            aria-label={formatValue(value)}
            className="flex size-6 cursor-grab items-center justify-center gap-0.5 rounded-full border border-gray-200 bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)] focus:outline-none active:cursor-grabbing"
          >
            <span className="h-2.5 w-px bg-gray-400" />
            <span className="h-2.5 w-px bg-gray-400" />
          </Thumb>
        ))}
      </Root>

      <div className="pan-detail relative h-4 text-gray-600">
        <span className="absolute left-0">{formatValue(minimum)}</span>
        {values.map((value, index) => (
          <span
            key={index}
            className="absolute -translate-x-1/2"
            style={{ left: `${percentOf(value)}%` }}
          >
            {formatValue(value)}
          </span>
        ))}
        <span className="absolute right-0">{formatValue(maximum)}</span>
      </div>
    </div>
  )
}
